import * as Sentry from '@sentry/vue';

import { scrubBreadcrumbUrls, scrubEventUrls } from '@/utils/sentryUrlScrubbing';

/**
 * Patterns Sentry drops before sending.
 *
 * These belong here rather than in `beforeSend`: Sentry matches them against
 * the exception's `type`/`value` (see `getPossibleEventMessages`), whereas
 * `event.message` is unset on exception events - so a `beforeSend` check
 * against `event.message` can never fire for a thrown error.
 *
 * Exported for the test that runs this config through Sentry's real filter.
 */
export const ignoreErrors = [
  "Can't find variable: _AutofillCallbackHandler", // error in instagram browser
  /\(UNAUTHENTICATED\)/, // expired/invalid token on graphql requests, e.g. getMultipleBestPrices 401
  // Chunk-load failures right after a deploy: the browser still holds an index
  // referencing hashed assets that no longer exist. Not actionable in the app -
  // one wording per engine.
  /Failed to fetch dynamically imported module/, // Chromium
  /error loading dynamically imported module/, // Firefox
  /Importing a module script failed/, // Safari
  /^Event `Event` \(type=error\) captured as promise rejection$/, // WEBSITE-G6V, no stack
  /Object Not Found Matching Id:\d+, MethodName:/, // WEBSITE-75F, Outlook's link scanner
  // WEBSITE-P35: Meta in-app browser blocking the GTM pixels. Has no stack, so the
  // gtm.js frame checks in `beforeSend` cannot catch it.
  /454: Handling is disabled/,
];

/**
 * Urls Sentry drops before sending.
 *
 * Matched against the last stack frame, not the page url - an event without a
 * stacktrace needs an `ignoreErrors` entry instead.
 *
 * Exported for the test that runs this config through Sentry's real filter.
 */
export const denyUrls = [
  'https://www.google-analytics.com/analytics.js',
  /sitecore\/shell\//,
  /sitecore%20modules\//,
  /^https?:\/\/(?:www\.)?fischer\.it\//,
  // Third-party widgets throwing inside their own SDK.
  /widget\.superchat\.de\//, // WEBSITE-HDD
  /embed\.tawk\.to\//, // WEBSITE-N07
];

export default function initSentry(app) {
  let beforeUnload = false;
  if (!IS_SSR) {
    window.addEventListener('beforeunload', () => {
      beforeUnload = true;
    });
  }

  Sentry.init({
    app,
    release: APP_ENVIRONMENT === 'PROD' ? FRONTEND_RELEASE : undefined,
    environment: APP_ENVIRONMENT,
    dsn: 'https://0745d0e0adc24364a1150a244bf9b0b8@sentry.io/2522235',
    ignoreErrors,
    denyUrls,
    // Runs for every breadcrumb as it is recorded - i.e. before it is ever attached
    // to an event - so fetch/xhr breadcrumbs (data.url) and navigation breadcrumbs
    // (data.to/data.from) never carry the migration link's id/validation params.
    beforeBreadcrumb(breadcrumb) {
      return scrubBreadcrumbUrls(breadcrumb);
    },
    beforeSend(event, hint) {
      // The default httpContext integration attaches request.url = the full page
      // URL to every event; scrub it (and any breadcrumb that reached the event by
      // some other path) before anything below can act on - or ship - it.
      scrubEventUrls(event);
      if (
        hint?.originalException?.statusCode === 401 &&
        hint?.originalException?.response?.url?.includes('graphql')
      ) {
        return null;
      }
      if (window.location.search.includes('debugSentry=true')) {
        window.debugSentry = { event, hint };
      }
      if (beforeUnload) {
        console.debug('before unload fired');
        return null;
      }
      if (hint?.originalException?.stack) {
        const isGoogleTagManager = /\(\/gtm\.js:/;
        if (isGoogleTagManager.test(hint.originalException.stack)) {
          return null;
        }
      }
      const cancelException = event?.exception?.values?.some((exception) => {
        return exception?.stacktrace?.frames?.some((frame) => frame.filename?.includes('/gtm.js'));
      });
      if (cancelException) {
        return null;
      }
      return event;
    },
  });
  Sentry.attachErrorHandler(app, { logErrors: true });
}
